import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DoramaCard } from "../components/DoramaCard";
import { DoramaPreviewModal } from "../components/DoramaPreviewModal";
import { supabase } from "../lib/supabase";
import { categoryIcons, categoryName, formatPrice, formatViews, isDoramaFree } from "../lib/catalog";
import type { Dorama } from "../types";

const categories = [
  ["romance", "Romance"],
  ["action", "Ação"],
  ["drama", "Drama"],
  ["asian", "Asiáticos"],
  ["hot", "Hot"],
  ["turkish", "Turcos"],
  ["brazilian", "Brasileiro"],
] as const;

export function HomePage() {
  const [doramas, setDoramas] = useState<Dorama[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDorama, setSelectedDorama] = useState<Dorama | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let active = true;
    supabase
      .from("doramas")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data, error: requestError }) => {
        if (!active) return;
        if (requestError) setError("Não foi possível carregar o catálogo agora.");
        else setDoramas((data || []) as Dorama[]);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const featured = doramas[0];
  const grouped = useMemo(
    () => new Map(categories.map(([key]) => [key, doramas.filter((item) => item.category === key)])),
    [doramas],
  );

  return (
    <>
      <section className="hero" style={featured?.hero_image_url || featured?.cover_url ? { backgroundImage: `url(${featured.hero_image_url || featured.cover_url})` } : undefined}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <p className="hero-category">{categoryIcons[featured?.category || ""] || "🎬"} {categoryName(featured?.category || null)}</p>
          <h1>{featured?.title || "Doramas e novelinhas em português"}</h1>
          {featured && <div className="hero-meta"><span className="hero-rating"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z"/></svg>{featured.rating ?? "—"}</span><span>{featured.year ?? "—"}</span><span>{featured.content_type === "complete" ? "Completo • Todos Episódios" : `${featured.episodes ?? 0} episódios`}</span><span className="hero-views"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0Z"/><circle cx="12" cy="12" r="3"/></svg><b>{formatViews(featured)}</b><small>visualizações</small></span></div>}
          <p>{featured?.description || "Pagamento único, sem mensalidade e com acesso imediato."}</p>
          {featured && <Link className="hero-price" to={`/dorama/${featured.slug || featured.id}`}>{isDoramaFree(featured.free_until) ? "GRÁTIS" : formatPrice(featured.price)}</Link>}
        </div>
      </section>

      <div className="catalog-container">
        {loading && <div className="status-box" role="status">Carregando catálogo…</div>}
        {error && <div className="status-box error" role="alert">{error}</div>}
        {!loading && !error && doramas.length === 0 && <div className="status-box">Nenhum título publicado.</div>}

        {categories.map(([key, label]) => {
          const items = grouped.get(key) || [];
          return (
            <section className="category-section" id={key} key={key}>
              <h2><span aria-hidden="true">{categoryIcons[key]}</span><span>{label}</span></h2>
              {items.length ? <><div className="card-grid">
                {(expanded[key] ? items : items.slice(0, 6)).map((dorama) => <DoramaCard dorama={dorama} onPreview={setSelectedDorama} key={dorama.id} />)}
              </div>{items.length > 6 && <button className="show-more-button" type="button" onClick={() => setExpanded((current) => ({ ...current, [key]: !current[key] }))}>{expanded[key] ? "Mostrar menos" : `Ver todos de ${categoryName(key)}`} <span aria-hidden="true">›</span></button>}</> : null}
            </section>
          );
        })}
      </div>
      <DoramaPreviewModal key={selectedDorama?.id || "closed"} dorama={selectedDorama} open={Boolean(selectedDorama)} onClose={() => setSelectedDorama(null)} />
    </>
  );
}
